import { range } from 'es-toolkit';
import { createSelector } from 'reselect';

import { Member } from '@paragon/contract-library';
import { Direction2D, distanceEquals, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { calculateMemberOverallLengthSegment } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { getActiveComponent } from './getActiveComponentSelector';
import { getInsertingObjectInfo } from './insertionObjectSelectors';

export interface SpliceSnap {
  member: Member;
  location: Point2D;
  distance: number;
  segment: LineSegment2D;
  thickness: number;
}

export const getSpliceInsertionSnaps = createSelector(
  getInsertingObjectInfo,
  getActiveComponent,
  (insertingObjectInfo, component): readonly SpliceSnap[] | null => {
    if (!component || insertingObjectInfo?.type !== 'splice') return null;

    const membersOfInterest = insertingObjectInfo.memberGuid
      ? component.members.filter(({ guid }) => guid === insertingObjectInfo.memberGuid)
      : component.members.filter(({ memberType }) => memberType === 'TopChord' || memberType === 'BottomChord');

    return membersOfInterest
      .map((member) => {
        const segmentOrNull = calculateMemberOverallLengthSegment(member, component);
        if (!segmentOrNull) return null;
        const segment = LineSegment2D.fromSegmentObject(segmentOrNull).orientWith(Direction2D.right);
        // It's a current limitation of the server code that vertical (and near-vertical) chords cannot be spliced. For
        // now we avoid offering to splice these.
        if (segment.direction.isParallelTo(Direction2D.up)) return null;

        const length = segment.length;
        const spliceIncrements = Math.ceil(length / 12) - 1;
        const memberLengthIsInWholeFeet = distanceEquals((spliceIncrements + 1) * 12, length);

        const splicesFromLeft = range(1, spliceIncrements + 1).map((spliceInFeet) => spliceInFeet * 12);
        const splicesFromRight = memberLengthIsInWholeFeet
          ? []
          : range(1, spliceIncrements + 1).map((spliceInFeet) => length - spliceInFeet * 12);

        const memberWidth = member.relativeGeometry?.width ?? 3.5;
        const halfWidthTranslation = segment.direction.rotateQuarterTurn().toVector(memberWidth / 2);

        return splicesFromLeft
          .concat(splicesFromRight)
          .toSorted((a, b) => a - b)
          .map((spliceDistance): SpliceSnap => {
            const location = segment.getPointAt(spliceDistance)!;
            return {
              member,
              location,
              distance: spliceDistance,
              segment: new LineSegment2D(
                location.translate(halfWidthTranslation),
                location.translate(halfWidthTranslation.reverse()),
              ),
              thickness: member.thickness,
            };
          });
      })
      .filter(notNullish)
      .flat();
  },
);
